import { UserData } from 'src/api/auth/@user-data';
import { IExtendedTransactionParticipants, TransactionQuestionnaireFinance } from 'src/redux/types';
import { IQuestionnaireTransaction } from 'src/types';
import { getTransactionSellersHolders } from 'src/utils/transaction';

export const sellerFinance = (
	participants: IExtendedTransactionParticipants,
	transactionQuestionnaire: IQuestionnaireTransaction,
	user: UserData,
	finance: TransactionQuestionnaireFinance,
) => {
	const proprietors = getTransactionSellersHolders(participants, transactionQuestionnaire);

	return {
		logoPosition: 'right',
		pages: [
			{
				title: 'Ownership',
				name: 'ownership',
				elements: [
					{
						type: 'radiogroup',
						name: 'ownership_type',
						title: 'How do you own your property? *',
						hideNumber: true,
						isRequired: true,
						choices: ['Owned outright', 'Owned with a mortgage', 'Help-to-buy', 'Shared ownership'],
						showOtherItem: true,
					},
					{
						type: 'html',
						name: 'notice_sale_note',
						visibleIf: "{ownership_type} = 'Owned with a mortgage'",
						html: "Thank you. We'll ask you to upload a copy of your latest <b>Annual Mortgage Statement</b> in the <b>Documents</b> tab.",
					},
				],
			},
			{
				name: 'banking',
				title: 'Banking',
				elements: [
					...finance.sellers.map((s) => {
						const isCurrentUser = user.uid === s.id;

						return {
							type: 'panel',
							name: s.id,
							title: `${s.givenNames} ${s.lastNames}${isCurrentUser ? ' *' : ''}`,
							...(isCurrentUser && { description: 'Please select any bank accounts you intend to use for this sale' }),
							readOnly: !isCurrentUser && !user.isAdmin,
							isRequired: true,
							elements: [
								{
									type: 'tagbox',
									name: `${s.id}_accounts`,
									title: `Bank accounts`,
									description:
										'Select all bank accounts you intend to use for this transaction. To add a new account, open your profile (⚙️ icon in the top-right) and select the Bank Accounts tab.',
									choices: s.bankAccounts.map((a) => ({ text: a.label, value: a.id })),
								},
								{
									type: 'checkbox',
									name: `${s.id}_waiver`,
									title: `Waiver`,
									visibleIf: `{${s.id}_accounts} empty and ${finance.sellers.length} > 1`,
									requiredIf: `{${s.id}_accounts} empty and ${finance.sellers.length} > 1`,
									hideNumber: true,
									choices: [
										{
											value: true,
											text: 'I do not plan to use any bank accounts in my name.',
										},
									],
								},
							],
						};
					}),
					{
						type: 'html',
						name: 'bank_accounts_note',
						html: '<p>To meet compliance requirements, for each of the accounts listed above, you must provide:</p><ul><li>• One statement for each of the <b>last 3 months</b></li><li>• One statement <b>going back at least 12 months</b></li></ul>',
					},
				],
			},
			{
				name: 'charges',
				elements: [
					{
						type: 'paneldynamic',
						name: 'mortgages',
						title: 'Mortgages *',
						description: 'Which mortgages are currently associated with this property?',
						hideNumber: true,
						isRequired: true,
						templateElements: [
							{
								type: 'panel',
								name: 'mortgage',
								elements: [
									{
										type: 'tagbox',
										name: 'holders',
										title: 'Mortgage holder(s) *',
										isRequired: true,
										choices: proprietors,
									},
									{
										type: 'text',
										name: 'lender',
										title: 'Lender *',
										titleLocation: 'left',
										isRequired: true,
									},
									{
										type: 'text',
										name: 'mortgage_account_number',
										title: 'Mortgage account number *',
										titleLocation: 'left',
										hideNumber: true,
										isRequired: true,
										validators: [
											{
												type: 'regex',
												text: 'Invalid entry: Please provide a genuine input.',
												regex: '^(?=.*\\d)[A-Za-z\\d@$!%*?&]{8,}$',
											},
										],
									},
									{
										type: 'text',
										name: 'amount_borrowed',
										title: 'Amount borrowed *',
										titleLocation: 'left',
										description: 'In Pounds',
										hideNumber: true,
										isRequired: true,
										maskType: 'numeric',
										min: 1000,
										maskSettings: { allowNegativeValues: false },
									},
									{
										type: 'text',
										name: 'balance',
										title: 'Current mortgage balance *',
										titleLocation: 'left',
										description: 'In Pounds',
										hideNumber: true,
										isRequired: true,
										maskType: 'numeric',
										maskSettings: { allowNegativeValues: false },
									},
								],
								title: 'Mortgage details',
								isRequired: true,
							},
						],
						noEntriesText: '',
						panelAddText: 'Add mortgage',
					},
				],
				title: 'Charges',
				visibleIf: "{ownership_type} = 'Owned with a mortgage'",
			},
		],
		completeText: 'Submit',
	};
};
